import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supervisorMonitoringService } from '../services/SupervisorMonitoringService';
import type {
  SupervisorSession,
  SupervisorAction,
  ActiveCallMonitoring,
  SupervisorActiveSession,
  SupervisionType,
} from '../types/supervisor-monitoring';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const supervisorMonitoringKeys = {
  all: ['supervisorMonitoring'] as const,
  activeSessions: (companyId: string) =>
    [...supervisorMonitoringKeys.all, 'activeSessions', companyId] as const,
  session: (id: string) =>
    [...supervisorMonitoringKeys.all, 'session', id] as const,
  sessionActions: (sessionId: string) =>
    [...supervisorMonitoringKeys.all, 'sessionActions', sessionId] as const,
  activeCalls: (companyId: string) =>
    [...supervisorMonitoringKeys.all, 'activeCalls', companyId] as const,
  callDetails: (callId: string) =>
    [...supervisorMonitoringKeys.all, 'callDetails', callId] as const,
  supervisorActive: (supervisorId: string) =>
    [...supervisorMonitoringKeys.all, 'supervisorActive', supervisorId] as const,
};

// ============================================================================
// ACTIVE SESSIONS HOOKS
// ============================================================================

export function useActiveSessions(companyId: string) {
  return useQuery({
    queryKey: supervisorMonitoringKeys.activeSessions(companyId),
    queryFn: () => supervisorMonitoringService.getActiveSessions(companyId),
    refetchInterval: 5000, // Refresh every 5 seconds
  });
}

export function useSession(id: string) {
  return useQuery({
    queryKey: supervisorMonitoringKeys.session(id),
    queryFn: () => supervisorMonitoringService.getSessionById(id),
    enabled: !!id,
  });
}

export function useSupervisorActiveSessions(supervisorId: string) {
  return useQuery({
    queryKey: supervisorMonitoringKeys.supervisorActive(supervisorId),
    queryFn: () =>
      supervisorMonitoringService.getSupervisorActiveSessions(supervisorId),
    refetchInterval: 3000, // Refresh every 3 seconds
    enabled: !!supervisorId,
  });
}

// ============================================================================
// SESSION MUTATIONS
// ============================================================================

export function useStartMonitoring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      supervisorId,
      callId,
      conferenceName,
      sessionType,
    }: {
      companyId: string;
      supervisorId: string;
      callId: string;
      conferenceName: string;
      sessionType?: SupervisionType;
    }) =>
      supervisorMonitoringService.startMonitoring(
        companyId,
        supervisorId,
        callId,
        conferenceName,
        sessionType
      ),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: supervisorMonitoringKeys.activeSessions(variables.companyId),
      });
      queryClient.invalidateQueries({
        queryKey: supervisorMonitoringKeys.supervisorActive(variables.supervisorId),
      });
    },
  });
}

export function useChangeSessionType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      fromType,
      toType,
    }: {
      sessionId: string;
      fromType: SupervisionType;
      toType: SupervisionType;
    }) =>
      supervisorMonitoringService.changeSessionType(sessionId, fromType, toType),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: supervisorMonitoringKeys.session(data.id),
      });
      queryClient.invalidateQueries({
        queryKey: supervisorMonitoringKeys.activeSessions(data.company_id),
      });
      queryClient.invalidateQueries({
        queryKey: supervisorMonitoringKeys.supervisorActive(data.supervisor_id),
      });
    },
  });
}

export function useEndSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      supervisorMonitoringService.endSession(sessionId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: supervisorMonitoringKeys.session(data.id),
      });
      queryClient.invalidateQueries({
        queryKey: supervisorMonitoringKeys.activeSessions(data.company_id),
      });
      queryClient.invalidateQueries({
        queryKey: supervisorMonitoringKeys.supervisorActive(data.supervisor_id),
      });
    },
  });
}

// ============================================================================
// ACTIONS HOOKS
// ============================================================================

export function useSessionActions(sessionId: string) {
  return useQuery({
    queryKey: supervisorMonitoringKeys.sessionActions(sessionId),
    queryFn: () => supervisorMonitoringService.getSessionActions(sessionId),
    enabled: !!sessionId,
  });
}

export function useLogAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      actionType,
      fromState,
      toState,
      notes,
    }: {
      sessionId: string;
      actionType: string;
      fromState?: string | null;
      toState?: string | null;
      notes?: string | null;
    }) =>
      supervisorMonitoringService.logAction(
        sessionId,
        actionType,
        fromState,
        toState,
        notes
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: supervisorMonitoringKeys.sessionActions(variables.sessionId),
      });
    },
  });
}

// ============================================================================
// ACTIVE CALLS HOOKS
// ============================================================================

export function useActiveCallsForMonitoring(companyId: string) {
  return useQuery({
    queryKey: supervisorMonitoringKeys.activeCalls(companyId),
    queryFn: () =>
      supervisorMonitoringService.getActiveCallsForMonitoring(companyId),
    refetchInterval: 3000, // Refresh every 3 seconds
  });
}

export function useCallDetails(callId: string) {
  return useQuery({
    queryKey: supervisorMonitoringKeys.callDetails(callId),
    queryFn: () => supervisorMonitoringService.getCallDetails(callId),
    enabled: !!callId,
  });
}

// ============================================================================
// REALTIME SUBSCRIPTIONS
// ============================================================================

export function useActiveCallsRealtime(companyId: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!companyId) return;

    const unsubscribe = supervisorMonitoringService.subscribeToActiveCalls(
      companyId,
      () => {
        // Invalidate active calls query on any change
        queryClient.invalidateQueries({
          queryKey: supervisorMonitoringKeys.activeCalls(companyId),
        });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [companyId, queryClient]);
}

export function useSupervisorSessionsRealtime(companyId: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!companyId) return;

    const unsubscribe = supervisorMonitoringService.subscribeToSupervisorSessions(
      companyId,
      () => {
        // Invalidate sessions queries on any change
        queryClient.invalidateQueries({
          queryKey: supervisorMonitoringKeys.activeSessions(companyId),
        });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [companyId, queryClient]);
}

// ============================================================================
// HELPER HOOKS
// ============================================================================

export function useSupervisionTypeDescription(type: SupervisionType): string {
  return supervisorMonitoringService.getSupervisionTypeDescription(type);
}

export function useCanPerformAction(
  currentType: SupervisionType,
  targetType: SupervisionType
): boolean {
  return supervisorMonitoringService.canPerformAction(currentType, targetType);
}
